import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import {
  Control,
  Controller,
  FieldValues,
  Path,
  UseFormClearErrors,
  UseFormSetValue,
  UseFormWatch,
  useFieldArray,
} from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { getCustomSchemas } from '../api/users-api';

import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';

export type ProfileFieldOption = {
  display: string;
  value: string;
  fieldType: string;
  multiValued?: boolean;
  schemaName?: string;
};

// Backward-compatible export used by policy components (Primary Organization fields only)
export const profileFieldOptions = [
  {
    display: 'Location',
    value: 'primaryOrganizationLocation',
  },
  {
    display: 'Job Title',
    value: 'primaryOrganizationJobTitle',
  },
  {
    display: 'Cost Center',
    value: 'primaryOrganizationCostCenter',
  },
  {
    display: 'Type of Employee',
    value: 'primaryOrganizationTypeOfEmployee',
  },
  {
    display: 'Stock Symbol',
    value: 'primaryOrganizationStockSymbol',
  },
  {
    display: 'Name',
    value: 'primaryOrganizationName',
  },
  {
    display: 'Department',
    value: 'primaryOrganizationDepartment',
  },
];

// Full Employee Information field list used by the admin filter UI
export const employeeInfoFields: ProfileFieldOption[] = [
  { display: "Manager's Email", value: 'managersEmail', fieldType: 'EMAIL' },
  { display: 'Employee ID', value: 'employeeId', fieldType: 'TEXT' },
  { display: 'cPanel Role', value: 'cpanelRole', fieldType: 'CPANEL_ROLE' },
  { display: 'gPanel Role', value: 'gPanelRole', fieldType: 'TEXT' },
  { display: 'Recovery Phone', value: 'recoveryPhone', fieldType: 'TEXT' },
  { display: 'Recovery Email', value: 'recoveryEmail', fieldType: 'TEXT' },
];

// Calendar sharing fields for condition blocks
export const calendarSharingFields: ProfileFieldOption[] = [
  {
    display: 'user internal to domain',
    value: 'sharedToUserInternalToDomain',
    fieldType: 'BOOL',
  },
  {
    display: 'user external to domain',
    value: 'sharedToUserExternalToDomain',
    fieldType: 'BOOL',
  },
  {
    display: 'specific user',
    value: 'specificUser',
    fieldType: 'EMAIL',
  },
  {
    display: 'domain',
    value: 'domain',
    fieldType: 'TEXT',
  },
  {
    display: 'specific group',
    value: 'sharedToGroups',
    fieldType: 'EMAIL',
  },
];

export const profileInfoTypeOptions = [
  { label: 'Employee Information', value: 'employeeInformation' },
  { label: 'Primary Organization', value: 'primaryOrganization' },
  { label: 'Custom Attributes', value: 'customAttributes' },
];

interface SharedProfileFieldFilterProps<T extends FieldValues> {
  control: Control<T>;
  watch: UseFormWatch<T>;
  setValue: UseFormSetValue<T>;
  clearErrors?: UseFormClearErrors<T>;
  baseFieldName: string;
  disabled?: boolean;
}

export function resolveFieldType(
  profileFieldType: string,
  customOptions: ProfileFieldOption[]
): string {
  const empField = employeeInfoFields.find((f) => f.value === profileFieldType);
  if (empField) return empField.fieldType;
  const custField = customOptions.find((f) => f.value === profileFieldType);
  if (custField) {
    // Map API types to UI types
    switch (custField.fieldType) {
      case 'STRING':
        return 'TEXT';
      case 'INT64':
        return 'WHOLE_NUMBER';
      case 'DOUBLE':
        return 'DECIMAL';
      case 'DATE':
        return 'DATE';
      case 'BOOL':
        return 'BOOL';
      case 'EMAIL':
        return 'EMAIL';
      case 'PHONE':
        return 'PHONE';
      default:
        return 'TEXT';
    }
  }
  const primaryOrgField = profileFieldOptions.find(
    (f) => f.value === profileFieldType
  );
  return primaryOrgField ? 'TEXT' : 'TEXT';
}

export default function SharedProfileFieldFilter<T extends FieldValues>({
  control,
  watch,
  setValue,
  clearErrors,
  baseFieldName,
  disabled,
}: SharedProfileFieldFilterProps<T>) {
  const profileInfoType = watch(`${baseFieldName}.profileInfoType` as Path<T>);
  const profileFieldType = watch(
    `${baseFieldName}.profileFieldType` as Path<T>
  );
  const valueInputMode = watch(`${baseFieldName}.valueInputMode` as Path<T>);

  const { fields, append, remove } = useFieldArray({
    control,
    name: `${baseFieldName}.profileFieldValues` as never,
  });

  const { data: customSchemas, isLoading: schemasLoading } = useQuery({
    queryKey: ['customSchemas'],
    queryFn: getCustomSchemas,
    enabled: profileInfoType === 'customAttributes',
  });

  const customOptions: ProfileFieldOption[] = (customSchemas ?? []).flatMap(
    (schema) =>
      (schema.fields ?? []).map((field) => ({
        display: field.displayName,
        value: `customAttribute.${schema.schemaName}.${field.fieldName}`,
        fieldType: field.fieldType,
        multiValued: field.multiValued,
        schemaName: schema.displayName,
      }))
  );

  const primaryOrgOptions: ProfileFieldOption[] = profileFieldOptions.map(
    (f) => ({ ...f, fieldType: 'TEXT' })
  );

  const currentFieldOptions: ProfileFieldOption[] =
    profileInfoType === 'customAttributes'
      ? customOptions
      : profileInfoType === 'primaryOrganization'
      ? primaryOrgOptions
      : employeeInfoFields;

  const resolvedFieldType = profileFieldType
    ? resolveFieldType(profileFieldType as string, customOptions)
    : 'TEXT';

  // Check if the selected field is multi-valued (only for Custom Attributes)
  const selectedField = customOptions.find((f) => f.value === profileFieldType);
  const isMultiValued = selectedField?.multiValued ?? false;

  // Only allow regex for TEXT fields
  const supportsExact = resolvedFieldType === 'TEXT';

  // Only allow exact match for TEXT fields, excluding EMAIL
  const supportsRegex =
    resolvedFieldType === 'TEXT' &&
    customOptions.find((f) => f.value === profileFieldType)?.fieldType !==
      'EMAIL';
  const activeMode = (valueInputMode as string) ?? 'fieldValue';

  function handleProfileInfoTypeChange(newType: string) {
    setValue(`${baseFieldName}.profileInfoType` as Path<T>, newType as never);
    setValue(`${baseFieldName}.profileFieldType` as Path<T>, '' as never);
    setValue(`${baseFieldName}.fieldDataType` as Path<T>, '' as never);
    setValue(
      `${baseFieldName}.profileFieldValues` as Path<T>,
      [{ value: '' }] as never
    );
    setValue(`${baseFieldName}.profileFieldRegex` as Path<T>, '' as never);
    setValue(
      `${baseFieldName}.profileFieldValueExact` as Path<T>,
      false as never
    );
    setValue(
      `${baseFieldName}.valueInputMode` as Path<T>,
      'fieldValue' as never
    );
    clearErrors?.(`${baseFieldName}` as Path<T>);
  }

  function handleProfileFieldTypeChange(newValue: string) {
    setValue(`${baseFieldName}.profileFieldType` as Path<T>, newValue as never);
    setValue(
      `${baseFieldName}.fieldDataType` as Path<T>,
      resolveFieldType(newValue, customOptions) as never
    );
    setValue(
      `${baseFieldName}.profileFieldValues` as Path<T>,
      [{ value: '' }] as never
    );
    setValue(`${baseFieldName}.profileFieldRegex` as Path<T>, '' as never);
    setValue(
      `${baseFieldName}.profileFieldValueExact` as Path<T>,
      false as never
    );
    setValue(
      `${baseFieldName}.valueInputMode` as Path<T>,
      'fieldValue' as never
    );
    clearErrors?.(`${baseFieldName}` as Path<T>);
  }

  function handleModeChange(mode: string) {
    setValue(`${baseFieldName}.valueInputMode` as Path<T>, mode as never);
    if (mode === 'fieldValue') {
      setValue(`${baseFieldName}.profileFieldRegex` as Path<T>, '' as never);
      clearErrors?.(`${baseFieldName}.profileFieldRegex` as Path<T>);
    } else {
      setValue(
        `${baseFieldName}.profileFieldValues` as Path<T>,
        [{ value: '' }] as never
      );
      setValue(
        `${baseFieldName}.profileFieldValueExact` as Path<T>,
        false as never
      );
      clearErrors?.(`${baseFieldName}.profileFieldValues` as Path<T>);
    }
  }

  return (
    <Grid container spacing={2} alignItems="flex-start">
      {/* Profile Info Type */}
      <Grid size={{ xs: 14, md: 12 }}>
        <Controller
          name={`${baseFieldName}.profileInfoType` as Path<T>}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              select
              sx={{ width: 530 }}
              label="Profile Info Type"
              value={field.value ?? ''}
              onChange={(e) => handleProfileInfoTypeChange(e.target.value)}
              disabled={disabled}
              error={!!error}
              helperText={error?.message || ' '}
            >
              {profileInfoTypeOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      </Grid>

      {/* Profile Field */}
      <Grid size={{ xs: 14, md: 12 }}>
        <Controller
          name={`${baseFieldName}.profileFieldType` as Path<T>}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Autocomplete
              value={
                currentFieldOptions.find((opt) => opt.value === field.value) ??
                null
              }
              onChange={(_e, newVal) =>
                handleProfileFieldTypeChange(newVal?.value ?? '')
              }
              disabled={disabled || !profileInfoType}
              disablePortal
              options={currentFieldOptions}
              loading={schemasLoading}
              groupBy={
                profileInfoType === 'customAttributes'
                  ? (opt) => opt.schemaName ?? ''
                  : undefined
              }
              getOptionLabel={(opt) => opt.display}
              isOptionEqualToValue={(opt, val) => opt.value === val?.value}
              renderOption={(props, opt) => (
                <li {...props} key={opt.value}>
                  {opt.display}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={
                    profileInfoType === 'customAttributes'
                      ? 'Custom Attribute Field'
                      : 'Profile Field'
                  }
                  error={!!error}
                  sx={{ width: 530 }}
                  helperText={error?.message || ' '}
                  slotProps={{
                    input: {
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {schemasLoading ? (
                            <CircularProgress color="inherit" size={18} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    },
                  }}
                />
              )}
            />
          )}
        />
      </Grid>
      {
        <Grid size={{ xs: 12 }}>
          {/* Field Value row */}
          <Stack
            direction="row"
            alignItems={isMultiValued ? 'flex-start' : 'center'}
            gap={1}
          >
            {supportsRegex && (
              <Radio
                checked={activeMode === 'fieldValue'}
                onChange={() => handleModeChange('fieldValue')}
                disabled={disabled || !profileFieldType}
                size="small"
              />
            )}

            {/* Multi-value rows for multi-valued Custom Attributes; single row for all others */}
            {isMultiValued ? (
              <Stack flex={1} gap={1}>
                {fields.map((fieldItem, index) => {
                  const isLastField = index === fields.length - 1;
                  return (
                    <Stack
                      key={fieldItem.id}
                      direction="row"
                      alignItems="center"
                      gap={1}
                    >
                      <FieldValueInput
                        control={control}
                        name={
                          `${baseFieldName}.profileFieldValues.${index}.value` as Path<T>
                        }
                        fieldType={resolvedFieldType}
                        disabled={disabled || activeMode !== 'fieldValue'}
                      />
                      {supportsExact && (
                        <Tooltip
                          arrow
                          title="When checked, the Field Value must be a case-sensitive match."
                          disableHoverListener={index !== 0}
                        >
                          <FormControlLabel
                            sx={{
                              mb: 2.5,
                              visibility: index === 0 ? 'visible' : 'hidden',
                            }}
                            control={
                              <Controller
                                name={
                                  `${baseFieldName}.profileFieldValueExact` as Path<T>
                                }
                                control={control}
                                render={({ field }) => (
                                  <Checkbox
                                    {...field}
                                    checked={!!field.value}
                                    disabled={
                                      disabled ||
                                      activeMode !== 'fieldValue' ||
                                      !profileFieldType
                                    }
                                  />
                                )}
                              />
                            }
                            label="Exact"
                          />
                        </Tooltip>
                      )}
                      {isLastField && (
                        <Tooltip title="Add another value" arrow>
                          <IconButton
                            size="small"
                            onClick={() => append({ value: '' } as never)}
                            disabled={disabled || activeMode !== 'fieldValue'}
                            sx={{ mb: 2.5 }}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {fields.length > 1 && (
                        <Tooltip title="Remove this value" arrow>
                          <IconButton
                            size="small"
                            onClick={() => remove(index)}
                            disabled={disabled || activeMode !== 'fieldValue'}
                            sx={{ mb: 2.5 }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  );
                })}
              </Stack>
            ) : (
              <FieldValueInput
                control={control}
                name={`${baseFieldName}.profileFieldValues.0.value` as Path<T>}
                fieldType={resolvedFieldType}
                disabled={
                  disabled || activeMode !== 'fieldValue' || !profileFieldType
                }
              />
            )}

            {!isMultiValued && supportsExact && (
              <Tooltip
                arrow
                title="When checked, the Field Value must be a case-sensitive match."
              >
                <FormControlLabel
                  sx={{ mb: 2.5 }}
                  control={
                    <Controller
                      name={
                        `${baseFieldName}.profileFieldValueExact` as Path<T>
                      }
                      control={control}
                      render={({ field }) => (
                        <Checkbox
                          {...field}
                          checked={!!field.value}
                          disabled={
                            disabled ||
                            activeMode !== 'fieldValue' ||
                            !profileFieldType
                          }
                        />
                      )}
                    />
                  }
                  label="Exact"
                />
              </Tooltip>
            )}
          </Stack>

          {/* Regex row – only for TEXT fields */}
          {supportsRegex && (
            <Stack direction="row" alignItems="flex-start" gap={1}>
              <Radio
                checked={activeMode === 'regex'}
                onChange={() => handleModeChange('regex')}
                disabled={disabled || !profileFieldType}
                size="small"
                sx={{ mt: 0.5 }}
              />
              <Controller
                name={`${baseFieldName}.profileFieldRegex` as Path<T>}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Tooltip
                    arrow
                    title="A regular expression is a sequence of characters that defines a search pattern. For example, to match values starting with 'Phil', use `^Phil`."
                  >
                    <TextField
                      {...field}
                      label="Regular Expression"
                      variant="outlined"
                      value={field.value ?? ''}
                      disabled={disabled || activeMode !== 'regex'}
                      error={!!error}
                      helperText={error?.message || ' '}
                      sx={{ minWidth: 410 }}
                    />
                  </Tooltip>
                )}
              />
            </Stack>
          )}
        </Grid>
      }
    </Grid>
  );
}

export interface FieldValueInputProps<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  fieldType: string;
  disabled?: boolean;
  onValueChange?: (val: string) => void;
  sx?: any;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
}

export function FieldValueInput<T extends FieldValues>({
  control,
  name,
  fieldType,
  disabled,
  onValueChange,
  sx,
  fullWidth,
  size = 'medium',
}: FieldValueInputProps<T>) {
  const getHelperText = (error: any) => {
    return error?.message || (size === 'small' ? undefined : ' ');
  };

  switch (fieldType) {
    case 'DATE':
      return (
        <Controller
          name={name}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <DatePicker
              label="Date Value"
              value={field.value ? dayjs(field.value as string) : null}
              onChange={(date) => {
                const val = date ? date.format('YYYY-MM-DD') : '';
                field.onChange(val);
                onValueChange?.(val);
              }}
              disabled={disabled}
              slotProps={{
                textField: {
                  error: !!error,
                  helperText: getHelperText(error),
                  sx: { minWidth: 530, ...sx },
                  fullWidth: fullWidth,
                  size: size,
                },
              }}
            />
          )}
        />
      );

    case 'WHOLE_NUMBER':
      return (
        <Controller
          name={name}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label="Number"
              type="number"
              value={field.value ?? ''}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '' || /^-?\d+$/.test(raw)) {
                  field.onChange(raw);
                  onValueChange?.(raw);
                }
              }}
              slotProps={{ htmlInput: { step: 1 } }}
              disabled={disabled}
              error={!!error}
              helperText={getHelperText(error)}
              sx={{ minWidth: 530, ...sx }}
              fullWidth={fullWidth}
              size={size}
            />
          )}
        />
      );

    case 'BOOL':
      return (
        <Controller
          name={name}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              select
              label="Field Value"
              value={field.value ?? ''}
              onChange={(e) => {
                field.onChange(e);
                onValueChange?.(e.target.value);
              }}
              disabled={disabled}
              error={!!error}
              helperText={getHelperText(error)}
              sx={{ minWidth: 530, ...sx }}
              fullWidth={fullWidth}
              size={size}
            >
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </TextField>
          )}
        />
      );

    case 'EMAIL':
      return (
        <Controller
          name={name}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label="Email"
              type="email"
              value={field.value ?? ''}
              onChange={(e) => {
                field.onChange(e);
                onValueChange?.(e.target.value);
              }}
              disabled={disabled}
              error={!!error}
              helperText={getHelperText(error)}
              sx={{ minWidth: 530, ...sx }}
              fullWidth={fullWidth}
              size={size}
            />
          )}
        />
      );

    case 'PHONE':
      return (
        <Controller
          name={name}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label="Phone Number"
              type="tel"
              value={field.value ?? ''}
              onChange={(e) => {
                field.onChange(e);
                onValueChange?.(e.target.value);
              }}
              disabled={disabled}
              error={!!error}
              helperText={getHelperText(error)}
              sx={{ minWidth: 530, ...sx }}
              fullWidth={fullWidth}
              size={size}
            />
          )}
        />
      );

    case 'CPANEL_ROLE':
      return (
        <Controller
          name={name}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Autocomplete
              value={field.value ?? null}
              onChange={(_e, val) => {
                field.onChange(val);
                onValueChange?.(val as string);
              }}
              disabled={disabled}
              options={['Admin', 'User']}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Field Value"
                  error={!!error}
                  helperText={getHelperText(error)}
                  sx={{ minWidth: 530, ...sx }}
                  fullWidth={fullWidth}
                  size={size}
                />
              )}
            />
          )}
        />
      );

    case 'DECIMAL':
      return (
        <Controller
          name={name}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label="Number"
              type="number"
              value={field.value ?? ''}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '' || /^-?\d*\.?\d*$/.test(raw)) {
                  field.onChange(raw);
                  onValueChange?.(raw);
                }
              }}
              slotProps={{ htmlInput: { step: 'any' } }}
              disabled={disabled}
              error={!!error}
              helperText={getHelperText(error)}
              sx={{ minWidth: 530, ...sx }}
              fullWidth={fullWidth}
              size={size}
            />
          )}
        />
      );

    default:
      return (
        <Controller
          name={name}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              label="Field Value"
              value={field.value ?? ''}
              onChange={(e) => {
                field.onChange(e);
                onValueChange?.(e.target.value);
              }}
              disabled={disabled}
              error={!!error}
              helperText={getHelperText(error)}
              sx={{ minWidth: 410, ...sx }}
              fullWidth={fullWidth}
              size={size}
            />
          )}
        />
      );
  }
}
